import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { setCookie, getCookie, removeCookie } from '../actions/auth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);

  const apiBase = process.env.REACT_APP_API_URL || '';

  // Check for existing token on mount
  useEffect(() => {
    const token = getCookie('token');
    if (token) {
      // Validate token by fetching user
      axios.get(`${apiBase}/api/users/user`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => {
          setUser(res.data);
          setIsAuthenticated(true);
          setIsLoading(false);
        })
        .catch(() => {
          // Token expired or invalid, try refresh
          const refreshToken = sessionStorage.getItem('refreshToken');
          if (refreshToken) {
            axios.post(`${apiBase}/api/auth/refresh`, { refresh_token: refreshToken })
              .then(res => {
                setCookie('token', res.data.token);
                sessionStorage.setItem('refreshToken', res.data.refresh_token);
                setUser(res.data.user);
                setIsAuthenticated(true);
                setIsLoading(false);
              })
              .catch(() => {
                removeCookie('token');
                sessionStorage.removeItem('refreshToken');
                setIsAuthenticated(false);
                setIsLoading(false);
              });
          } else {
            removeCookie('token');
            setIsAuthenticated(false);
            setIsLoading(false);
          }
        });
    } else {
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  }, [apiBase]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const response = await axios.post(`${apiBase}/api/auth/login`, { email, password });
      const { token, refresh_token, user } = response.data;
      setCookie('token', token);
      sessionStorage.setItem('refreshToken', refresh_token);
      // Clear any prior account's cached identity so downstream getUser() fetches
      // THIS user (and their full business) instead of returning a stale cached one.
      sessionStorage.removeItem('user');
      setUser(user);
      setIsAuthenticated(true);
      return user;
    } catch (err) {
      const message = err.response?.data?.error || 'Login failed. Please try again.';
      setError(message);
      throw new Error(message);
    }
  }, [apiBase]);

  const logout = useCallback(() => {
    removeCookie('token');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('ssoUser');
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Session inactivity timeout (15 minutes)
  useEffect(() => {
    if (!isAuthenticated) return;

    const isSso = sessionStorage.getItem('ssoUser') === 'true';
    const TIMEOUT_MS = isSso ? 20 * 60 * 1000 : 15 * 60 * 1000;
    let timer;

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        // Clear storage directly and do a full page reload
        // to avoid black screen from component unmounting
        sessionStorage.clear();
        window.location.replace('/login');
      }, TIMEOUT_MS);
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, resetTimer));
    resetTimer();

    return () => {
      clearTimeout(timer);
      events.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, [isAuthenticated, logout]);

  const value = {
    isAuthenticated,
    isLoading,
    user,
    error,
    login,
    logout,
    setError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
